/**
 * O ROMANEIO, NA TELA E NO PAPEL — a parte que a Despesca e os Romaneios
 * compartilham.
 *
 * Morava dentro da Despesca, e por isso só existia enquanto o formulário
 * estava aberto: gravada a despesca, o papel sumia junto. Foi o que o gestor
 * achou em 06/10/2026, ao despescar o T-2 — «ao salvar a despesca não tive
 * acesso ao pdf, e não encontrei ele em canto algum». Agora as duas telas
 * mostram o mesmo romaneio, com as mesmas saídas, porque é o mesmo código.
 */

import { emReais, type Romaneio } from '../dominio/romaneio'
import { baixarCsv, nomeDeArquivo } from '../dominio/planilha'
import { diaCurto, inteiro, numero } from '../dominio/unidades'
import { SaidasDePapel } from './Papel'
import { RomaneioPapel, type CabecalhoDoRomaneio } from './RomaneioPapel'

/**
 * O romaneio na tela, com os dois pesos lado a lado.
 *
 * O pesado em cima e o cobrado embaixo, sempre nessa ordem e sempre os dois.
 * Mostrar só o cobrado esconderia que houve abatimento; mostrar só o pesado
 * faria a conta do dinheiro não fechar com o papel do comprador.
 */
export function RomaneioNaTela({
  romaneio, semValores = false,
}: {
  romaneio: Romaneio
  /** Para o tratador nas listas de romaneio: pesos sim, dinheiro não (06/10/2026). */
  semValores?: boolean
}) {
  return (
    <div className="romaneio">
      <table>
        <thead>
          {/* A unidade mora no cabeçalho, não em cada célula: «255,5 kg»
              repetido em dez linhas empurrava a coluna do VALOR para fora da
              tela de 375 px — justamente a coluna que quem vende procura. */}
          <tr>
            <th>espécie</th>
            <th>peixes</th>
            <th>pesado kg</th>
            <th>desconto kg</th>
            <th>cobrado kg</th>
            {!semValores && <th>R$</th>}
          </tr>
        </thead>
        <tbody>
          {romaneio.linhas.map((l) => (
            <tr key={l.especie}>
              <td data-rotulo="espécie">{l.especie}</td>
              <td data-rotulo="peixes">{inteiro(l.peixes)}</td>
              <td data-rotulo="pesado">{numero(l.pesadoKg, 2)} kg</td>
              <td data-rotulo="desconto">{numero(l.descontoKg, 2)} kg</td>
              <td data-rotulo="cobrado">{numero(l.cobradoKg, 2)} kg</td>
              {!semValores && (
                <td data-rotulo="valor">
                  {l.valorBrl === null ? '—' : emReais(l.valorBrl)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th data-rotulo="">total</th>
            <th data-rotulo="peixes">{inteiro(romaneio.peixes)}</th>
            <th data-rotulo="pesado">{numero(romaneio.pesadoKg, 2)} kg</th>
            <th data-rotulo="desconto">{numero(romaneio.descontoKg, 2)} kg</th>
            <th data-rotulo="cobrado">{numero(romaneio.cobradoKg, 2)} kg</th>
            {!semValores && (
              <th data-rotulo="valor">
                {romaneio.valorBrl === null ? '—' : emReais(romaneio.valorBrl)}
              </th>
            )}
          </tr>
        </tfoot>
      </table>

      {!semValores && romaneio.valorBrl !== null && (
        <p className="romaneio-total">{emReais(romaneio.valorBrl)}</p>
      )}

      <p className="secundario">
        {numero(romaneio.mediaG / 1000, 3)} kg por peixe · desconto de{' '}
        {numero(romaneio.descontoPct, 1)}% ={' '}
        {numero(romaneio.descontoKg, 2)} kg abatidos
      </p>

      {!semValores && romaneio.valorBrl === null && (
        <p className="secundario">
          Falta o preço de alguma espécie — o total só aparece quando todas têm preço, porque
          total pela metade engana mais que total ausente.
        </p>
      )}
    </div>
  )
}

/**
 * O papel que viaja — a planilha do romaneio e o «imprimir / PDF». As duas
 * saídas, e o porquê de serem só duas, estão em `SaidasDePapel`.
 */
export function ExportarRomaneio({
  romaneio, cabecalho, semValores = false,
}: {
  romaneio: Romaneio
  cabecalho: CabecalhoDoRomaneio
  semValores?: boolean
}) {
  function planilha(): boolean {
    const linhas: (string | number | null)[][] = [
      ['Romaneio de despesca'],
      ['Fazenda', 'Cunhataí'],
      ['Represa', cabecalho.represa],
      ['Ciclo', cabecalho.ciclo],
      ['Data', diaCurto(cabecalho.dia)],
      ['Comprador', cabecalho.comprador],
      ['Destino', cabecalho.destino],
      ['Desconto de umidade (%)', romaneio.descontoPct],
      [],
      ['Espécie', 'Peixes', 'Pesado (kg)', 'Desconto (kg)', 'Líquido (kg)',
       ...(semValores ? [] : ['Preço (R$/kg)', 'Valor (R$)'])],
      ...romaneio.linhas.map((l) => [
        l.especie, l.peixes, l.pesadoKg, l.descontoKg, l.cobradoKg,
        ...(semValores ? [] : [l.precoKg, l.valorBrl]),
      ]),
      ['TOTAL', romaneio.peixes, romaneio.pesadoKg, romaneio.descontoKg,
       romaneio.cobradoKg, ...(semValores ? [] : [null, romaneio.valorBrl])],
      [],
      ['Peso médio (kg por peixe)', arredondar(romaneio.mediaG / 1000, 3)],
    ]
    return baixarCsv(
      nomeDeArquivo('romaneio', cabecalho.ciclo || cabecalho.represa || 'despesca', cabecalho.dia),
      linhas,
    )
  }

  return <SaidasDePapel aoPlanilha={planilha} />
}

/**
 * O romaneio inteiro, aberto: a tabela, as saídas e o papel.
 *
 * O PAPEL É UM SÓ NA PÁGINA. Ele vai para o corpo do documento por portal, e
 * a impressão mostra todo `.papel` que houver — dois romaneios montados ao
 * mesmo tempo sairiam os dois na mesma folha. Quem usa este componente abre
 * um de cada vez.
 */
export function RomaneioAberto({
  romaneio, cabecalho, semValores = false,
}: {
  romaneio: Romaneio
  cabecalho: CabecalhoDoRomaneio
  semValores?: boolean
}) {
  return (
    <>
      <RomaneioNaTela romaneio={romaneio} semValores={semValores} />
      <ExportarRomaneio romaneio={romaneio} cabecalho={cabecalho} semValores={semValores} />
      <RomaneioPapel romaneio={romaneio} cabecalho={cabecalho} semValores={semValores} />
    </>
  )
}

function arredondar(v: number, casas: number): number {
  const f = 10 ** casas
  return Math.round(v * f) / f
}
